import React, { createContext, useMemo, useSyncExternalStore } from 'react'
import { authStatusStore } from 'src/queries/authStatusStore'

export type SessionStatus = {
  /**
   * Raw authenticated-session flag; not gated on the user having loaded. For
   * "authenticated AND user loaded", use UserProvider's `useAuthedUser`.
   */
  isAuthenticated: boolean
  /**
   * Raw CSP-operator session flag, with the same not-gated-on-user caveat.
   * Not the emulated session — emulating a user sets `isAuthenticated`, and
   * ending the emulation leaves this flag set. Either flag can be true without
   * the other.
   */
  isCspAuthenticated: boolean
}

export const SessionStatusContext = createContext<SessionStatus | undefined>(
  undefined,
)

/**
 * Reactive context for the raw session flags, so components subscribe to them
 * instead of reading Redux directly. Distinct from UserProvider's user-resolved
 * `authenticated` (which also requires the user to have loaded); do not fold them
 * together.
 *
 * The golden-path read source is `authStatusStore` (see src/context/AGENTS.md),
 * byte-equal to state.auth — primed from the hydrated state at boot, then kept
 * in step by the write-through middleware. Both reads MUST stay reactive
 * (useSyncExternalStore, never a bare getSnapshot()) or the auth render gates freeze.
 */
export const SessionStatusProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isAuthenticated = useSyncExternalStore(
    authStatusStore.subscribe,
    authStatusStore.getSnapshot,
    authStatusStore.getSnapshot,
  )
  const isCspAuthenticated = useSyncExternalStore(
    authStatusStore.subscribe,
    authStatusStore.getCspAuthSnapshot,
    authStatusStore.getCspAuthSnapshot,
  )
  const value = useMemo<SessionStatus>(
    () => ({ isAuthenticated, isCspAuthenticated }),
    [isAuthenticated, isCspAuthenticated],
  )
  return (
    <SessionStatusContext.Provider value={value}>
      {children}
    </SessionStatusContext.Provider>
  )
}
